<template>
  <div class="flex flex-row gap-4 relative">
    <div class="relative" v-if="showDateRangePanel">
      <SmartTimeRangePanel v-model="range" @input="handleSelectTimeRange" />
      <el-tooltip content="Close Date Range" placement="top">
        <i
          class="el-icon-close cursor-pointer absolute top-4 right-4"
          @click="toggleDateRangePanel"
        >
        </i>
      </el-tooltip>
    </div>
    <el-form
      class="flex flex-col absolute-time-range-form gap-3"
      label-position="top"
      ref="formRef"
      :model="form"
      :rules="rules"
    >
      <div>Absolute Time Range</div>
      <div>From</div>
      <el-form-item label="" class="flex-1" prop="startValue">
        <el-input
          v-model="form.startValue"
          placeholder="Select start date"
          class="flex-1"
        >
          <template #append>
            <i
              class="el-icon-date cursor-pointer"
              @click="toggleDateRangePanel"
            >
            </i>
          </template>
        </el-input>
      </el-form-item>
      <div>To</div>
      <el-form-item label="" class="flex-1" prop="endValue">
        <el-input
          v-model="form.endValue"
          placeholder="Select end date"
          class="flex-1"
        >
          <template #append>
            <i
              class="el-icon-date cursor-pointer"
              @click="toggleDateRangePanel"
            >
            </i>
          </template>
        </el-input>
      </el-form-item>
      <div>Time Interval</div>
      <el-form-item label="">
        <div class="flex flex-row gap-2">
          <el-input-number
            class="flex-1"
            v-model="intervalValue"
            :min="1"
            :max="9999"
            style="width: 100px"
          />
          <el-select  v-model="intervalUnit" style="width: 100px" :popper-append-to-body="false">
            <el-option label="seconds" value="s" />
            <el-option label="minutes" value="min" />
            <el-option label="hours" value="h" />
            <el-option label="days" value="d" />
            <el-option label="weeks" value="w" />
            <el-option label="months" value="mo" />
          </el-select>
        </div>
      </el-form-item>
      <el-form-item>
        <div class="flex flex-row items-center overflow-hidden">
          <el-button type="primary" icon="el-icon-check" @click="validateForm">
            Apply time range
          </el-button>
          <el-tooltip
            content="Reset to Relative Time Range, Enable Zoom In, Zoom Out, and Refresh buttons"
            placement="top"
          >
            <el-button
              icon="el-icon-refresh"
              @click="handleReset"
              class="flex-1"
            >
              Reset
            </el-button>
          </el-tooltip>
        </div>
      </el-form-item>
      <el-form-item>
        <div class="flex flex-row items-center overflow-hidden"></div>
      </el-form-item>
    </el-form>
    <div class="flex flex-col gap-2 range-list" v-if="!showDateRangePanel">
      <div>
        <el-input
          v-model="searchKeywords"
          clearable
          placeholder="Search quick ranges"
        >
          <template #prepend><i class="el-icon-search"></i></template>
        </el-input>
      </div>
      <el-scrollbar class="flex-1 scroll-bar">
        <div
          v-for="(label, key) in filteredDateRangeKeyValues"
          :key="key"
          class="select-item"
          :class="{ selected: val === key }"
          @click="selectRange(key)"
        >
          {{ label }}
        </div>
      </el-scrollbar>
    </div>
    <el-button
      class="close-button"
      icon="el-icon-close"
      @click="handleClose"
    ></el-button>
  </div>
</template>

<script>
import SmartTimeRangePanel from "@/components/SmartControls/TimeRangePanel/SmartTimeRangePanel.vue";
import DateRangeKeyValues from "./date-range-key-values";
import dayjs from "dayjs";
import getTimeInterval from "./getTimeInterval";

export default {
  name: "AbsoluteTimeRangeView",
  components: { SmartTimeRangePanel },
  emits: ["update:start", "update:end", "confirm", "cancel", "resize"],
  props: {
    start: {
      type: String,
      default: "",
    },
    end: {
      type: String,
      default: "",
    },
  },
  computed: {
    DateRangeKeyValues: () => DateRangeKeyValues,
    filteredDateRangeKeyValues() {
      if (!this.searchKeywords) return DateRangeKeyValues;
      const kw = this.searchKeywords.toLowerCase();
      const result = {};
      Object.entries(DateRangeKeyValues).forEach(([key, label]) => {
        if (label.toLowerCase().includes(kw)) {
          result[key] = label;
        }
      });
      return result;
    },
    range: {
      get() {
        return [this.form.startValue, this.form.endValue];
      },
      set(value) {
        this.form.startValue = dayjs(value[0])
          .startOf("d")
          .format("YYYY-MM-DDTHH:mm:ss");
        this.form.endValue = dayjs(value[1])
          .endOf("d")
          .format("YYYY-MM-DDTHH:mm:ss");
        this.val = "";
      },
    },
  },
  data() {
    return {
      showDateRangePanel: false,
      form: {
        startValue: this.start,
        endValue: this.end,
      },
      val: "",
      searchKeywords: "",
      intervalValue: 1,
      intervalUnit: "h",
      rules: {
        startValue: [
          {
            required: true,
            message: "Start date is required",
            trigger: "blur",
          },
        ],
        endValue: [
          { required: true, message: "End date is required", trigger: "blur" },
        ],
      },
    };
  },
  watch: {
    showDateRangePanel() {
      this.$emit("resize");
    },
  },
  methods: {
    toggleDateRangePanel() {
      this.showDateRangePanel = !this.showDateRangePanel;
    },
    selectRange(key) {
      this.val = key;
      this.form.startValue = key;
      this.form.endValue = "now";
      this.computeTimeInterval();
      this.$refs.formRef.validate();
    },
    validateForm() {
      this.$refs.formRef.validate((valid) => {
        if (valid) {
          this.handleConfirm();
        }
      });
    },
    handleConfirm() {
      this.$emit("confirm", {
        start: this.form.startValue,
        end: this.form.endValue,
        interval: `${this.intervalValue}${this.intervalUnit}`,
      });
    },
    parseDateRangeKey(key) {
      if (!key || typeof key !== "string") return null;
      if (key.startsWith("now-")) {
        const match = key.match(/^now-(\d+)([mhd])$/);
        if (match) {
          const value = parseInt(match[1], 10);
          const unit = match[2];
          let ms = 0;
          if (unit === "m") ms = value * 60 * 1000;
          if (unit === "h") ms = value * 60 * 60 * 1000;
          if (unit === "d") ms = value * 24 * 60 * 60 * 1000;
          return Date.now() - ms;
        }
      }
      return null;
    },
    computeTimeInterval() {
      const { startValue: startTimeStr, endValue: endTimeStr } = this.form;
      const { intervalValue, intervalUnit } = getTimeInterval(
        startTimeStr,
        endTimeStr
      );
      this.intervalValue = intervalValue;
      this.intervalUnit = intervalUnit;
    },
    handleSelectTimeRange() {
      this.computeTimeInterval();
      this.$refs.formRef.validate();
    },
    handleClose() {
      this.$emit("cancel");
    },
    handleReset() {
      this.$emit("reset");
    },
  },
};
</script>
<style lang="scss" scoped>
@import "@/styles/element-variables.scss";
.select-item {
  padding: 8px;
  color: white;
  cursor: pointer;
  &:hover {
    background-color: $--border-color-lighter;
  }
  &.selected {
    color: $--color-primary;
    background-color: $--background-color-base;
  }
}

.range-list {
  width: 200px;
}

.absolute-time-range-form {
  .el-form-item {
    margin-bottom: 0;
  }
}

.close-button {
  position: absolute;
  top: -12px;
  right: -12px;
  transform: translateX(100%);
}
</style>
